import { useMemo } from "react";
import { scopeFor, ScopeNotReadyError, type QueryScope } from "@/queries/scope";
import { useStore } from "@/stores/store-context";

/**
 * The query scope (contract §2) for `tenant` under the signed-in user, or `null`
 * while there is no workspace or the session has not said who is signed in. A
 * component passes it to a query definition and leaves the query disabled
 * while it is `null`.
 *
 * The same object comes back while the tenant and the principal hold, so a
 * callback or effect that depends on the scope keeps its identity across
 * renders. A fresh object each render made `useFetchSkillUsers` a new function
 * every render, and the picker effect depending on it re-read and re-seeded in
 * a loop (NG-2802).
 *
 * Reads the signed-in user from the user store, so an `observer` component
 * re-renders, and picks the scope up, once the session resolves.
 */
export function useQueryScope(tenant: string | undefined): QueryScope | null {
  const { user } = useStore();
  const userId = user.currentUser?.id;
  const current = currentScope(tenant, userId);
  const scopeTenant = current?.tenant;
  const principal = current?.principal;
  return useMemo(
    () =>
      scopeTenant !== undefined && principal !== undefined
        ? { tenant: scopeTenant, principal }
        : null,
    [scopeTenant, principal],
  );
}

function currentScope(tenant: string | undefined, userId: string | undefined): QueryScope | null {
  if (!tenant || !userId) return null;
  try {
    return scopeFor(tenant);
  } catch (error) {
    if (error instanceof ScopeNotReadyError) return null;
    throw error;
  }
}
